/*
 * Figma `46:70` — Page Header: eyebrow, title, description, section tabs, primary action.
 *
 * It takes its content from the destination rather than from props, which is what stops a screen's
 * heading drifting from the menu item that reached it — both read the same registry entry.
 *
 * The primary action is capped at one by the type: `primaryAction` is a single node, not a list.
 * "Limit each work surface to one highest-emphasis action" is a rule that only holds if the
 * component cannot express two, so secondary controls go in `secondaryActions` and render quieter.
 *
 * Below 768px the row wraps under the title and the primary action goes full width, rather than
 * being hidden in favour of a sticky bar: the sticky bar is opt-in per screen, so hiding here made
 * the action disappear entirely on every screen that had not adopted one. A screen that does use
 * `StickyActions` passes no `primaryAction` and keeps the header clean.
 */
import type { ReactNode } from 'react';
import { SectionTabs } from '../ui';
import type { SectionTabItem } from '../ui';
import type { Destination } from '../destinations';

export function PageHeader<K extends string>({
  destination, phaseLabel, primaryAction, secondaryActions, tabs, activeTab, onSelectTab,
}: {
  destination: Destination;
  /** The destination group the screen sits in — "you are in 03 · ขายและส่งมอบ". */
  phaseLabel?: string;
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  tabs?: readonly SectionTabItem<K>[];
  activeTab?: K;
  onSelectTab?: (key: K) => void;
}) {
  return (
    <div className="jt-page-head">
      <div className="jt-page-head-inner">
        <div>
          {phaseLabel && <div className="jt-eyebrow">{phaseLabel}</div>}
          <h1 className="jt-page-title">{destination.title}</h1>
          <p className="jt-page-sub">{destination.description}</p>
        </div>
        {(primaryAction || secondaryActions) && (
          <div className="jt-page-actions">
            {secondaryActions}
            {primaryAction}
          </div>
        )}
      </div>
      {tabs && activeTab !== undefined && onSelectTab && tabs.length > 1 && (
        <div className="jt-page-tabs">
          <SectionTabs items={tabs} active={activeTab} onSelect={onSelectTab} label={destination.title} />
        </div>
      )}
    </div>
  );
}
